import
  React, { useRef }
from 'react';

import
  styled
from 'styled-components/macro';

import {
  DataStore,
  Environment,
  FontFamilies,
  FontSizes,
  Image,
  MediaQueries,
  Logger,
  Theme,
  isValidProviderResult
} from 'common';

import {
  Button,
  H2,
  Markdown,
  NumericSelect,
  Stepper,
  Text,
  PSmall,
  TextInput,
} from 'common/components';

import {
  withCommon,
} from 'common/hocs';

import {
  EventMobileTicketSummary
} from 'ui/event/components';

import {
  EventName,
  FullWidthContainer,
  MaxWidthContainer,
  StepperContainer,
} from 'ui/event/styled';

import {
  OrderProvider,
  PaymentProvider
} from 'providers';

import { getCheckoutSteps } from 'ui/event/eventUtils';

/** Keep per_ticket_custom_fields length aligned with quantity (charity add-ons on payment). */
const resizePerTicketCustomFields = (line, newCount) => {
  const defs = line.custom_fields;
  if (!defs?.length) {
    return undefined;
  }
  if (newCount <= 0) {
    return [];
  }

  const existing = Array.isArray(line.per_ticket_custom_fields)
    ? line.per_ticket_custom_fields
    : [];
  const next = [];
  for (let i = 0; i < newCount; i += 1) {
    if (existing[i]?.length) {
      next.push(existing[i]);
    } else {
      next.push(
        defs.map((f) => ({
          ...f,
          value: f.type === 'checkbox' ? false : '',
        }))
      );
    }
  }
  return next;
};

const Container = styled.div`
  display: flex;
  flex: 1;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  min-height: 80vh;
`;

const SummaryContainer = styled.div`
  background-color: ${Theme.Card.Background};
  border: ${Theme.Card.Border};
  box-shadow: ${Theme.Card.BoxShadow};
  border-radius: ${Theme.Card.BorderRadius};
  overflow: hidden;
`;

const CenterContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: ${props => props.padding || '20px'};
`;

const Information = styled.div`
  color: ${Theme.Text.Color};
  font-family: ${FontFamilies.Bold};
  padding: ${props => props.padding || `18px 20px 5px 20px`};
  font-size: ${FontSizes.Regular};

  @media only screen and ${MediaQueries.Md} {
    font-size: 1.1rem;
  }
`;

const Header = styled.div`
  color: ${Theme.Text.Color};
  font-family: ${FontFamilies.Bold};
  padding: 10px 20px;
  display: flex;
  justify-content: space-between;

  @media only screen and ${MediaQueries.Md} {
    font-size: ${FontSizes.Big};
    padding: 20px 20px 15px 20px;
  }
`;

const Divide = styled.div`
  background-color: ${Theme.Divider.Color};
  height: 1px;
`;

const EventPaymentUI = props => {

  const [showNoStock, setShowNoStock] = React.useState(false);
  const [showNoMerchStock, setShowNoMerchStock] = React.useState(false);
  const [timeLeft, setTimeLeft] = React.useState(15 * 60); // 15 minutes
  const [paymentProvider, setPaymentProvider] = React.useState('');
  const [couponCode, setCouponCode] = React.useState('');
  const [appliedCoupon, setAppliedCoupon] = React.useState(null);
  const [couponError, setCouponError] = React.useState('');
  const [isApplyingCoupon, setIsApplyingCoupon] = React.useState(false);
  const orderIdRef = useRef(null);

  const [state, setState] = React.useState({
    event: DataStore.get('selectedEvent') || {},
    charityPrices: (DataStore.get('selectedEvent') || {})?.prices?.filter(p => p.type === 'charity') || [],
    selectedTickets: DataStore.get('selectedTickets')
  });

  const hasSeating = state.event?.has_seating;

  const isValidObject = (obj) => {
    return !!obj && Object.keys(obj).length > 0;
  };

  const isPageValid = () => {

    if (!isValidObject(DataStore.get('selectedTickets')?.selectedTickets) && !isValidObject(DataStore.get('selectedTickets')?.selectedMerch) && !isValidObject(DataStore.get('selectedTickets')?.selectedPackages)) {
      return false;
    }
  
    if (isValidObject(DataStore.get('selectedTickets')?.selectedTickets) && !DataStore.get('selectedEvent')?.id) {
      return false;
    }

    return true;
  };

  const calculatePricesWithCoupon = (selectedTickets, coupon) => {
    // Create a deep copy to avoid mutating the original
    const updatedTickets = JSON.parse(JSON.stringify(selectedTickets));

    if (!coupon || !coupon.items || !Array.isArray(coupon.items)) {
      return updatedTickets;
    }

    // Apply coupon discounts
    let totalPrice = 0;
    let totalTickets = 0;

    // First, calculate base prices for all tickets
    for (const ticketKey in updatedTickets.selectedTickets) {
      const ticket = updatedTickets.selectedTickets[ticketKey];
      ticket.totalPrice = ticket.count * ticket.price;
      totalTickets += ticket.count;
    }

    // Apply discounts from coupon items
    for (const couponItem of coupon.items) {
      const eventPriceId = couponItem.event_price_id;
      const ticket = updatedTickets.selectedTickets[eventPriceId];

      if (!ticket || ticket.count === 0) {
        continue;
      }

      // Calculate how many tickets get the discount (min of quantity_limit and selected count)
      const discountQuantity = Math.min(couponItem.quantity_limit, ticket.count);
      
      // Calculate discount amount per ticket
      let discountPerTicket = 0;
      if (couponItem.percent_discount) {
        discountPerTicket = (ticket.price * couponItem.percent_discount) / 100;
      } else if (couponItem.fixed_discount) {
        discountPerTicket = couponItem.fixed_discount;
      }

      // Apply discount to the eligible tickets
      const totalDiscount = discountPerTicket * discountQuantity;
      ticket.totalPrice = (ticket.count * ticket.price) - totalDiscount;
    }

    // Calculate final totals
    for (const item of Object.values(updatedTickets.selectedTickets || {})) {
      totalPrice += item.totalPrice;
    }

    for (const item of Object.values(updatedTickets.selectedMerch || {})) {
      totalPrice += item.totalPrice;
      totalTickets += item.count;
    }

    updatedTickets.price = totalPrice;
    updatedTickets.tickets = totalTickets;
    return updatedTickets;
  };

  const onCharityCounterChange = (count, priceId) => {

    const price = state.charityPrices.find(cp => cp.id === priceId);

    if (!price) {
      return;
    }

    const sTickets = { ...state.selectedTickets };
    sTickets.selectedTickets = { ...sTickets.selectedTickets };

    const mergedLine = {
      ...price,
      ...(sTickets.selectedTickets[priceId] || {}),
      count,
      totalPrice: count * price.price,
    };
    const resized = resizePerTicketCustomFields(mergedLine, count);
    if (resized !== undefined) {
      mergedLine.per_ticket_custom_fields = resized;
    } else {
      delete mergedLine.per_ticket_custom_fields;
    }
    sTickets.selectedTickets[priceId] = mergedLine;

    // adding try catch just for testing purposes
    try {
      const selectedTicketValues = Object.values(sTickets.selectedTickets);

      if (Array.isArray(selectedTicketValues) && selectedTicketValues.length > 0) {
        sTickets.price = selectedTicketValues.reduce((acc, ticket) => acc + ticket.totalPrice, 0);
      }
    }
    catch (error) {
      console.error('EventPayment', 'onCharityCounterChange', error);
    }

    // Recalculate prices with coupon if applied
    const updatedTickets = calculatePricesWithCoupon(sTickets, appliedCoupon);

    setState(prev => ({
      ...prev,
      selectedTickets: updatedTickets
    }));
  };

  const onPayClick = async () => {

    try {
      props.common.showLoading('Placing your order...');

      const selectedTickets = state.selectedTickets;
      const selectedEvent = DataStore.get('selectedEvent');

      if (!isValidObject(selectedTickets?.selectedTickets) && !isValidObject(selectedTickets?.selectedMerch) && !isValidObject(selectedTickets?.selectedPackages)) {
        throw Error('No tickets or merch selected');
      }
      else if (isValidObject(selectedTickets?.selectedTickets) && !selectedEvent?.id) {
        throw Error('Event details not found, please go back and try again.  We apologize for the inconvenience.');
      }

      const tickets = [];

      for (const ticket of Object.values(selectedTickets.selectedTickets || {})) {

        const linePayload = {
          event_price_id: ticket.id,
          quantity: ticket.count,
        };

        // Backend: one entry per generated ticket; align with order API contract.
        if (
          ticket.count > 0 &&
          Array.isArray(ticket.per_ticket_custom_fields) &&
          ticket.per_ticket_custom_fields.length === ticket.count
        ) {
          linePayload.per_ticket_custom_fields =
            ticket.per_ticket_custom_fields;
        }

        tickets.push(linePayload);
      }

      const merchList = [];

      for (const merch of Object.values(selectedTickets.selectedMerch || {})) {

        merchList.push({
          product_id: merch.product_id,
          product_variant_id: merch.product_variant_id,
          quantity: merch.count
        });
      }

      const packageList = [];

      for (const pkg of Object.values(selectedTickets.selectedPackages || {})) {

        const variantList = [];

        for (const [key, value] of Object.entries(pkg.variants)) {
          variantList.push(...Object.values(value).map(v => ({...v, package_item_id: parseInt(key)})));
        }

        packageList.push({
          package_id: pkg.packageId,
          quantity: pkg.count,
          variants: variantList
        });
      }

      const couponCode = appliedCoupon?.code || null;
      const orderRes = await OrderProvider.createOrder(
        selectedEvent?.id,
        tickets,
        merchList,
        packageList,
        selectedEvent?.custom_fields || [],
        couponCode
      );

      if (!isValidProviderResult(orderRes)) {
        throw Error(orderRes.message || 'Unable to create order');
      }

      if (orderRes.data?.hasCouponError) {

        Logger.info('EventPayment', 'onPayClick', orderRes.data?.message);
        props.common.showToast(orderRes.data?.message || 'Your coupon code is not valid');
        props.common.hideLoading();
        return;
      }

      if (orderRes.data?.message === 'NO_MORE_STOCK') {

        props.common.hideLoading();
        Logger.error('EventPayment', 'onPayClick', 'No more stock');
        setShowNoStock(true);
        return;
      }

      if (orderRes.data?.message === 'NO_MORE_MERCH_STOCK') {

        props.common.hideLoading();
        Logger.error('EventPayment', 'onPayClick', 'No more merch stock');
        setShowNoMerchStock(true);
        return;
      }

      if (!orderRes.data.orderId) {
        throw Error(orderRes.message || 'Unable to create order');
      }

      orderIdRef.current = orderRes.data.orderId;

      if (orderRes.data.skip_payment) {

        props.common.navigate(`/payment-result/success?order_id=${orderRes.data.orderId}`);
        return;
      }

      const pProvider = selectedEvent?.payment_provider || orderRes.data.payment_provider;
      console.log('pProvider', pProvider);

      // Yoco and ikhokha implementation works the same way
      if (pProvider === 'IKHOKHA' || pProvider === 'YOCO') {
        await createYocoPayment(orderRes.data.orderId);
      }
      else if (pProvider === 'PEACH') {
        await createPayment(orderRes.data.orderId, 'peach', orderRes.data.token);
      }
      else {
        await createPayment(orderRes.data.orderId);
      }
    }
    catch (ex) {

      Logger.error('EventPayment', 'onPayClick', ex);
      props.common.showToast('Oops! Something didn\'t go as planned. Please try again.');
      props.common.hideLoading();
    }
  };

  const createPayment = async (orderId, provider, token) => {

    try {

      let text = hasSeating ? 'Preparing your seat selection...' : 'Preparing your payment...';
      props.common.showLoading(text);

      const paymentRes = await PaymentProvider.checkout(orderId);

      if (provider !== 'peach' && (!isValidProviderResult(paymentRes) || !paymentRes.data.access_code)) {
        throw Error(paymentRes.message || 'Unable to create payment');
      }

      if (provider === 'peach' && (!isValidProviderResult(paymentRes) || !paymentRes.data.checkoutId || !paymentRes.data.entityId)) {
        throw Error(paymentRes.message || 'Unable to create payment');
      }

      const minutes = hasSeating ? 10 : 25;

      const expiryTime = new Date(Date.now() + minutes * 60 * 1000).getTime();

      let url = '';
      if (hasSeating && provider === 'peach') {
        // We should not do seating here
        return;
      }
      else if (provider === 'peach') {
        url = `/payment.html?checkout_id=${paymentRes.data.checkoutId}&entity_id=${paymentRes.data.entityId}&expiry=${expiryTime}&orderId=${orderId}&token=${token}`;
      }
      else if (hasSeating && provider !== 'peach') {
        // We should not do seating here
        return;
      }
      else {
        url = `/event/payment-frame?access_code=${paymentRes.data.access_code}&expiry=${expiryTime}&orderId=${orderId}`;
      }

      if (provider === 'peach') {

        const contact = DataStore.get('LOGGED_IN_EMAIL') || '';
        if (contact) {
          url += `&contact=${encodeURIComponent(contact)}`;
        }

        const returnUrl = props.common.isWidget
          ? `${window.location.origin}${window.location.pathname}#`
          : window.location.origin;

        url += `&returnUrl=${encodeURIComponent(returnUrl)}`;

        window.location.href = Environment.paymentHost + url;
        return;
      }

      props.common.navigate(url);
    }
    catch(e) {
      Logger.error('EventPayment', 'createPayment', e);
      throw e;
    }
  };

  const createYocoPayment = async (orderId) => {

    const timeout = setTimeout(() => {
      props.common.showLoading('Redirecting...');
    }, 4000);

    try {
      let text = hasSeating ? 'Preparing your seat selection...' : 'Preparing your payment...';
      props.common.showLoading(text);

      let currentUrl;

      if (props.common.isWidget) {
        currentUrl = `${window.location.origin}${window.location.pathname}#`;
      }

      const paymentRes = await PaymentProvider.checkout(orderId, currentUrl);

      if (!isValidProviderResult(paymentRes) || !paymentRes.data.redirect_url) {
        throw Error(paymentRes.message || 'Unable to create payment');
      }

      window.location.href = paymentRes.data.redirect_url;
    }
    catch(e) {
      Logger.error('EventPayment', 'createPayment', e);
      throw e;
    }
    finally {
      clearTimeout(timeout);
    }
  };

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds
      .toString()
      .padStart(2, '0')}`;
  };

  const onApplyCoupon = async () => {
    if (!couponCode.trim()) {
      setCouponError('Please enter a coupon code');
      return;
    }

    setIsApplyingCoupon(true);
    setCouponError('');

    try {
      const result = await OrderProvider.getCoupon(couponCode.trim(), state.event.id);

      if (!isValidProviderResult(result)) {
        setCouponError('Unable to validate coupon. Please try again.');
        Logger.error('EventPayment', 'onApplyCoupon', result.message);
        setAppliedCoupon(null);
        return;
      }

      if (!result.data.valid) {
        setCouponError(result.data.message || 'Invalid coupon code');
        setAppliedCoupon(null);
        return;
      }

      if (!Array.isArray(result.data.items) || result.data.items.length === 0) {
        setCouponError('Invalid coupon code');
        setAppliedCoupon(null);
        return;
      }

      // Success case - apply coupon discounts
      const couponData = {
        code: result.data.coupon_code || couponCode.trim(),
        items: result.data.items
      };

      setAppliedCoupon(couponData);

      if(couponError) {
        setCouponError('');
      }

      // Recalculate prices with coupon discounts
      const updatedTickets = calculatePricesWithCoupon(state.selectedTickets, couponData);

      setState(prev => ({
        ...prev,
        selectedTickets: updatedTickets
      }));

      Logger.info('EventPayment', 'onApplyCoupon', `Coupon applied: ${couponData.code}`);
    } catch (error) {
      Logger.error('EventPayment', 'onApplyCoupon', error);
      setCouponError('Unable to validate coupon. Please try again.');
      setAppliedCoupon(null);
    } finally {
      setIsApplyingCoupon(false);
    }
  };

  const onRemoveCoupon = () => {
    setCouponCode('');
    setAppliedCoupon(null);
    setCouponError('');

    // Recalculate prices without coupon discounts
    const updatedTickets = calculatePricesWithCoupon(DataStore.get('selectedTickets'), null);
    setState(prev => ({
      ...prev,
      selectedTickets: updatedTickets
    }));
  };

  React.useEffect(() => {

    if (timeLeft === 0) {
      props.common.navigate('/');
      return; // Stop the timer when it reaches 0
    }

    const intervalId = setInterval(() => {
      setTimeLeft((prevTime) => prevTime - 1);
    }, 1000);

    return () => clearInterval(intervalId); // Cleanup the interval on component unmount
  }, [timeLeft]); // eslint-disable-line

  React.useEffect(() => {

    PaymentProvider.getPaymentLogo()
      .catch(e => Logger.error('EventPayment', 'getPaymentLogo', e));

    const searchParams = new URLSearchParams(props.common.location.search);
    const retry = searchParams.get('retry');
    const orderId = searchParams.get('orderId');

    if (retry && orderId) {

      props.common.showLoading();
      createYocoPayment(parseInt(orderId));
    }

    if (!isPageValid()) {
      props.common.navigate('/');
    }
    else {
      setPaymentProvider(DataStore.get('selectedEvent')?.payment_provider || 'PEACH');
    }

  }, []); // eslint-disable-line

  React.useEffect(() => {
    const handlePageShow = (event) => {
      // Hide loading when page is shown (including from bfcache)
      props.common.hideLoading();
    };

    window.addEventListener('pageshow', handlePageShow);

    return () => {
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, []); // eslint-disable-line

  if (!isPageValid()) {
    return <></>
  }

  return (
    <>

      { !showNoStock && !showNoMerchStock &&
      
        <>
        <Text
          maxWidth={'1100px'}
          margin={'0 auto'}
          width={'100%'}
          display={'block'} 
          padding={'20px 15px 0 15px'} 
          fontSize={FontSizes.Small}
          color={Theme.Text.Color}
        >
          <span style={{cursor: 'pointer'}} onClick={() => props.common.navigate(-1)}>← Back</span>
        </Text>

        <EventName>{state.event.name}</EventName>

        <Text display={'block'} padding={'0 15px 5px 15px'} textAlign={'center'} fontSize={FontSizes.Small}>{state.event.location_display}</Text>

        { !state.event.is_multi_event &&
          <Text display={'block'} padding={'0 15px 20px 15px'} textAlign={'center'} fontSize={FontSizes.Small}>{state.event.displayDate}</Text>
        }
        { state.event.is_multi_event &&
          <Text display={'block'} padding={'0 0 30px 0'} textAlign={'center'} fontSize={FontSizes.Small}></Text>
        }

        <FullWidthContainer>

          <StepperContainer>

            <Stepper
              items={getCheckoutSteps(state.event)}
              selectedIndex={2}
            />

          </StepperContainer>

          <MaxWidthContainer padding={'0 15px 15px 15px'}>

            <CenterContainer
              padding={'5px 20px 20px 20px'}>

                <PSmall textAlign={'center'} padding={'0 0 3px 0'}>{ hasSeating ? 'Nice one! If you are happy with your order, click the button below to select your seats...' : 'Almost done! Final step...' }</PSmall>
                {false && <Text fontSize={FontSizes.Big} fontFamily={FontFamilies.Bold}>{formatTime(timeLeft)}</Text>}
            </CenterContainer>

            { Array.isArray(state.charityPrices) && state.charityPrices.length > 0 &&
              <>
                <SummaryContainer>
                  <Header>Donate to Charity?</Header>
                  <Divide/>
                  { state.charityPrices.map((cp, charityIndex) => (

                    <div key={`charity-${charityIndex}`} style={{ display: 'flex', flexDirection: 'column' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '18px 20px 15px 20px' }}>
                        <Information
                          padding={'00'}>
                          {cp.title}
                        </Information>
                        <NumericSelect
                          addButtonWidth={'70px'}
                          addButtonText={`Add R${cp.price}`}
                          max={cp.available_tickets}
                          onCounterChange={(count) => onCharityCounterChange(count, cp.id)}
                        />
                      </div>
                      <Markdown
                        defaultFontSize={FontSizes.Smaller}
                        padding={'0 20px 20px 20px'}
                        gap={'15px'}
                        markdown={cp.description}
                      />
                    </div>
                  ))}
                </SummaryContainer>
                <div style={{ height: '20px' }} />
              </>
            }

            { !!state.event.accept_coupons &&
              <>
                <SummaryContainer>
                  <Header>Redeem Coupon or Discount Code</Header>
                  <Divide/>
                  <div style={{ padding: '20px' }}>
                    <PSmall padding={'0 0 15px 0'} color={Theme.Text.Color}>
                      If you have a coupon or discount code, enter it below to apply a discount to your order.
                    </PSmall>
                    {!appliedCoupon ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                          <div style={{ flex: 1 }}>
                            <TextInput
                              placeholder="Enter coupon code"
                              margin={'0'}
                              value={couponCode}
                              onChange={(value) => {
                                setCouponCode(value.toUpperCase());
                                setCouponError('');
                              }}
                              width="100%"
                              hasError={!!couponError}
                              errorMessage={couponError}
                            />
                          </div>
                          <Button
                            text="Apply"
                            onClick={onApplyCoupon}
                            showLoading={isApplyingCoupon}
                            width="100px"
                            height="40px"
                          />
                        </div>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                          <Text fontSize={FontSizes.Small} fontFamily={FontFamilies.Bold}>
                            Coupon Applied: {appliedCoupon.code}
                          </Text>
                        </div>
                        <Button
                          text="Remove"
                          onClick={onRemoveCoupon}
                          width="100px"
                          height="40px"
                        />
                      </div>
                    )}
                  </div>
                </SummaryContainer>

                <div style={{ height: '20px' }} />
              </>
            }

            <SummaryContainer>

              <EventMobileTicketSummary
                totals={state.selectedTickets}
                hideCheckoutButton={true}
                coupon={appliedCoupon}
                >

                <Button
                  text={
                    hasSeating
                      ? 'Select Seats'
                      : (state.selectedTickets?.price === 0 ? 'Claim Order' :'Pay Now')
                  }
                  width={'100%'}
                  onClick={onPayClick}
                />
              </EventMobileTicketSummary>

            </SummaryContainer>

            <div style={{ height: '15px' }} />

            { !hasSeating && paymentProvider && 
            
              <CenterContainer>
                {(paymentProvider === 'YOCO' || paymentProvider === 'IKHOKHA') && <PSmall padding={'0 0 2px 0'}>Payments processed by</PSmall>}
                {paymentProvider === 'PAYSTACK' && <div style={{ height: '10px' }} />}

                <Image
                  alt={'payment processor'}
                  width={paymentProvider === 'YOCO' || paymentProvider === 'IKHOKHA'
                    ? '120px' 
                    : paymentProvider === 'PEACH'
                      ? '190px'
                      : '160px'}
                  height={'auto'}
                  borderRadius={paymentProvider === 'PEACH' ? '5px' : '0'}
                  src={
                    paymentProvider === 'YOCO'
                      ? 'https://storage.googleapis.com/website777/weTicket/weticket_site/yoco-logo-og-3.png'
                      : paymentProvider === 'IKHOKHA'
                        ? 'https://storage.googleapis.com/website777/weTicket/config/ik_logo.png'
                        : paymentProvider === 'PEACH'
                          ? `${Environment.assetBase}/images/secured_by_peach.png`
                          : 'https://storage.googleapis.com/website777/weTicket/config/paystack-badge-white.png'
                    }
                />
              </CenterContainer>
            }

          </MaxWidthContainer>

        </FullWidthContainer>
        </>
      }

      { showNoStock &&

        <Container>
          <H2>Tickets Unavailable</H2>
          <Text padding={'0 15px'} textAlign={'center'}>Unfortunately, the number of tickets you selected is no longer available.</Text>
          <Text padding={'10px 15px 25px 30px'} textAlign={'center'}>Please return to the ticket selection page to choose a different quantity.</Text>        
          <Button onClick={() => props.common.navigate('/')}>Home</Button>
          <div style={{ height: '50px' }} />
        </Container>
      }

      { showNoMerchStock &&

        <Container>
          <H2>Products Sold Out</H2>
          <Text padding={'0 15px'} textAlign={'center'}>Unfortunately, some of the products you tried to purchase are no longer available in the quantities you selected.</Text>
          <Text padding={'10px 15px 25px 30px'} textAlign={'center'}>Sorry for the inconvenience!</Text>
          <Button onClick={() => props.common.navigate('/')}>Home</Button>
          <div style={{ height: '50px' }} />
        </Container>
      }

    </>
  );
};

export const EventPayment = withCommon(EventPaymentUI, {
  showLoadingOnLoad: false
});